import {
  Component,
  h,
  Event,
  EventEmitter,
  State,
  ComponentInterface,
  Host,
  Prop,
} from "@stencil/core";
import { IThemeOptions } from "../../../models";
import {
  TranslateService,
  ThemeService,
  DateTimeFactory,
  Log,
  AppConfigService as Config,
  FormHelper,
  AppConfigService,
} from "../../../services";
import { ISchedule, IScheduleEntry, Schedule } from "@auvious/utils";
import { ScheduleService } from "../../../services/schedule.service";
import {
  ISCheduleContactBase,
  IScheduleRequest,
} from "../../../models/schedule/ISchedule";
import { NotificationService } from "../../../services/notification.service";
import moment from "moment";

interface IState {
  date?: Date;
  errors?: {
    date?: boolean;
    schedule?: string;
  };
  isLoading?: boolean;
  schedule?: Schedule;
}

const DEFAULT_STATE: IState = {
  date: null,
  errors: {},
  isLoading: false,
  schedule: null,
};

@Component({
  tag: "app-schedule-standard",
  styleUrl: "app-schedule-standard.scss",
  shadow: true,
})
export class AppScheduleStandard implements ComponentInterface {
  @Prop() contact: ISCheduleContactBase;
  @Prop() isDisclaimerAccepted: boolean;
  @Prop() scheduledAt: Date;

  @Event({ bubbles: false }) ready: EventEmitter<IScheduleRequest>;

  @State() state: IState = { ...DEFAULT_STATE };
  @State() themeOptions: IThemeOptions;

  private request: IScheduleRequest;
  // private schedule: Schedule = null;

  constructor() {
    ThemeService.optionsAvailable().subscribe((o) => (this.themeOptions = o));
  }

  componentWillLoad() {
    const schedule = new Schedule(Config.schedule);
    const proposedDateTime =
      this.scheduledAt || ScheduleService.getProposedDateTime();
    this.setState({ date: proposedDateTime, schedule });
    this.request = { start: proposedDateTime, customer: {} };
  }

  componentDidLoad(): void {}

  private async checkExistingAppointments() {
    const startAt = moment(this.state.date)
      .hours(0)
      .minutes(0)
      .seconds(0)
      .milliseconds(0)
      .toDate();

    const existingAppointments =
      await ScheduleService.getScheduledAppointmentsTimeframe(
        startAt,
        moment.duration(24, "hours")
      );

    if (existingAppointments.length > 0) {
      let scheduleConfig: ISchedule = { ...Config.schedule };

      // loop existing schedules and create a schedule entry for each
      existingAppointments.forEach((item) => {
        const entry: IScheduleEntry = {
          date: item.start.format("YYYY-MM-DD"),
          time: `${item.start.format("HH:mm")}/${item.start
            .add(item.duration)
            .format("HH:mm")}`,
          reason: "Overlapping appointment",
        };
        scheduleConfig = {
          ...scheduleConfig,
          schedule: {
            ...scheduleConfig.schedule,
            closed: [...(scheduleConfig.schedule.closed || []), entry],
          },
        };
      });

      this.setState({ schedule: new Schedule(scheduleConfig) });
    }
  }

  disconnectedCallback(): void {
    this.setState({
      ...DEFAULT_STATE,
      date: ScheduleService.getProposedDateTime(),
    });
  }

  private setState(state: IState) {
    this.state = { ...this.state, ...state };
  }

  async confirm() {
    if (this.isValid()) {
      try {
        if (
          !this.request ||
          !this.request.customer ||
          !this.request.customer?.name?.firstName ||
          !this.request.customer?.contact?.primaryPhone ||
          !this.request.start
        ) {
          this.setState({
            errors: {
              ...this.state.errors,
              schedule: TranslateService.instant(
                "Could not find contact information such as phone or schedule date. Please fill in the form again"
              ),
            },
          });
          return;
        }

        this.setState({ isLoading: true });

        const isOverlap = await ScheduleService.isOverlappingSchedule({
          ...this.request,
          start: this.state.date,
        });

        if (isOverlap) {
          NotificationService.warning("Please choose a different time", {
            body: "Another appointment has already been scheduled for that time.",
          });
          this.checkExistingAppointments();
          this.setState({ isLoading: false });
        } else {
          this.ready.emit({
            ...this.request,
            start: this.state.date,
          });
        }
      } catch (ex) {
        Log.error(ex);
      }
    }
  }

  timeChanged(event: CustomEvent) {
    Log.info("time changed", event.detail);
    // get only time and update
    const dt = DateTimeFactory.create(event.detail);
    const userTime = DateTimeFactory.create(this.state.date)
      .hour(dt.hour())
      .minute(dt.minute())
      .second(0)
      .toDate();
    //--
    this.setState({
      date: userTime,
      errors: { ...this.state.errors, date: null, schedule: null },
    });
    Log.info("new time", this.state.date);
  }

  dateChanged(event: CustomEvent<Date>) {
    Log.info("date changed", event.detail);

    this.setState({
      date: new Date(event.detail),
      errors: { ...this.state.errors, date: null, schedule: null },
    });

    this.checkExistingAppointments();

    Log.info("new time", this.state.date);
  }

  customerChanged(event: CustomEvent<ISCheduleContactBase>) {
    this.request = { ...this.request, customer: event.detail };
  }

  isValid(): boolean {
    let valid = FormHelper.isFormValid();

    // check date validity
    if (
      DateTimeFactory.create(this.state.date).isBefore(DateTimeFactory.create())
    ) {
      valid = false;
      this.setState({ errors: { ...this.state.errors, date: true } });
    }

    // check open hours
    const unit = this.state.schedule.getMinutes(
      this.state.date.getFullYear(),
      this.state.date.getMonth(),
      this.state.date.getDate(),
      this.state.date.getHours()
    )[this.state.date.getMinutes()];

    const open = unit.open;

    if (!open) {
      const reason =
        unit?.reasons?.join(" ") ||
        "We are closed at this time. Please check the working hours.";
      this.setState({
        errors: {
          ...this.state.errors,
          schedule: TranslateService.instant(reason),
        },
      });
      valid = false;
    }
    return valid;
  }

  render() {
    const confirmText = TranslateService.instant("Confirm");

    return (
      <Host>
        <div>
          <av-date-picker
            date={this.state.date}
            schedule={this.state.schedule}
            onDateChange={this.dateChanged.bind(this)}
          ></av-date-picker>
        </div>
        <hr />
        <div
          aria-label={TranslateService.instant("Pick a time")}
          role="group"
          class="form-field-horizontal"
        >
          <label>
            <av-translate>Time</av-translate>
          </label>
          <av-time-picker
            time={this.state.date}
            schedule={this.state.schedule}
            onTimeChange={this.timeChanged.bind(this)}
          ></av-time-picker>
        </div>
        <hr />

        {this.state.errors && this.state.errors.date && (
          <div class="form-field form-field-error-panel">
            <av-translate>Callback date should be later than now</av-translate>
          </div>
        )}

        <app-schedule-contact
          contact={this.contact}
          onContactChanged={this.customerChanged.bind(this)}
        ></app-schedule-contact>

        {this.state.errors?.schedule && (
          <div
            class="form-field form-field-error-panel"
            style={{ "margin-top": "10px" }}
          >
            {this.state.errors.schedule}
          </div>
        )}

        <div
          class={{
            actions: true,
            "actions-disclaimer": !!AppConfigService.scheduleDisclaimerURL,
          }}
        >
          {AppConfigService.scheduleDisclaimerURL && (
            <av-disclaimer
              url={AppConfigService.scheduleDisclaimerURL}
              accepted={this.isDisclaimerAccepted}
              onAcceptedChange={(e) => (this.isDisclaimerAccepted = e.detail)}
            ></av-disclaimer>
          )}
          <av-button
            data-tid="appointment/confirm"
            color="accent"
            disabled={!this.isDisclaimerAccepted}
            loading={this.state.isLoading}
            onClick={this.confirm.bind(this)}
          >
            {confirmText}
          </av-button>
        </div>
      </Host>
    );
  }
}
